import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { registerPlugin } from '@capacitor/core';
import { AuthPlugin } from '../../definitions';
import { StateSnackBarService } from './state-snack-bar.service';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

export interface TokenRefreshResponse {
  accessToken: string;
  refreshToken: string;
}

export interface UserInfo {
  id: number;
  username: string;
  email: string;
  roles: string[];
  token: string;
  refreshToken: string;
  profilePictureUrl: string;
  deviceToken?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private currentUserSubject = new BehaviorSubject<UserInfo | null>(null);
  private snackbarService = inject(StateSnackBarService);

  public currentUser$ = this.currentUserSubject.asObservable();

  Auth = registerPlugin<AuthPlugin>('Auth');

  constructor() {
    const userJson = localStorage.getItem('authUser');
    if (userJson) {
      this.currentUserSubject.next(JSON.parse(userJson));
    }
  }

  public get currentUserValue(): UserInfo | null {
    return this.currentUserSubject.value;
  }

  login(username: string, password: string): Observable<UserInfo> {
    return this.http
      .post<UserInfo>(
        environment.backend + '/auth/signin',
        {
          username,
          password,
        },
        httpOptions,
      )
      .pipe(
        tap((user) => {
          localStorage.setItem('authUser', JSON.stringify(user));
          this.currentUserSubject.next(user);
          if (user.deviceToken) {
            this.Auth.saveToken({ token: user.deviceToken });
          }
        }),
      );
  }

  logout(): void {
    localStorage.removeItem('authUser');
    this.Auth.deleteToken();
    this.currentUserSubject.next(null);

    this.router.navigate(['/login']);
  }

  register(username: string, email: string, password: string): Observable<any> {
    return this.http.post(
      environment.backend + '/auth/signup',
      {
        username,
        email,
        password,
      },
      httpOptions,
    );
  }

  public isLoggedIn(): boolean {
    const user = this.currentUserSubject.value;
    return user !== null;
  }

  public isAdmin(): boolean {
    const user = this.currentUserSubject.value;
    if (!user) {
      return false;
    }
    return user.roles.includes('ROLE_ADMIN');
  }

  public updateUserProfile(updatedData: Partial<UserInfo>): void {
    const currentUser = this.currentUserSubject.value;
    if (currentUser) {
      const newUser = { ...currentUser, ...updatedData };
      localStorage.setItem('authUser', JSON.stringify(newUser));
      this.currentUserSubject.next(newUser);
    }
  }

  refreshUser() {
    return this.http.get<UserInfo>(`${environment.backend}/auth/getRefreshedUser`, httpOptions).pipe(
      tap((user) => {
        localStorage.setItem('authUser', JSON.stringify(user));
        this.currentUserSubject.next(user);
      }),
      catchError((error) => {
        this.snackbarService.error({ message: 'Fehler beim Aktualisieren des Profils', subMessage: error.message });
        this.logout();
        return throwError(() => error);
      }),
    );
  }

  refreshToken(): Observable<TokenRefreshResponse> {
    const refreshToken = this.currentUserValue?.refreshToken;

    if (!refreshToken) {
      this.logout();
      throw new Error('No refresh token available');
    }

    return this.http
      .post<TokenRefreshResponse>(environment.backend + '/auth/refreshtoken', { refreshToken }, httpOptions)
      .pipe(
        tap((tokens) => {
          const user = this.currentUserValue;
          if (user) {
            user.token = tokens.accessToken; // Update only the access token
            localStorage.setItem('authUser', JSON.stringify(user));
            this.currentUserSubject.next(user);
            this.Auth.saveToken({ token: user.token });
          }
        }),
        catchError((error) => {
          this.logout();
          return throwError(() => error);
        }),
      );
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(environment.backend + '/auth/forgot-password', { email }, httpOptions);
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(environment.backend + '/auth/reset-password', { token, newPassword }, httpOptions);
  }
}
